<script>
  import { onMount } from 'svelte'
  import { fly, fade } from 'svelte/transition'
  import { quintInOut } from 'svelte/easing'
  import {
    snackBarStore,
    signatureBlockStore,
    userStore,
    workLogStore,
  } from '../state/store'
  import Icon from './Icon.svelte'
  import TextInput from './TextInput.svelte'
  import { request } from '../helpers/api'
  import { t } from '../i18n/i18n'

  export let locationId
  export let clientId
  let canvas
  let width = window.innerWidth - 40
  let height = '100px'
  let name = ''
  let note = ''
  let addNote = false
  $: noteCount = note.length
  let inFlight

  onMount(() => {
    const ctx = canvas.getContext('2d')
    ctx.strokeStyle = '#141414'
    ctx.lineWidth = 3
    ctx.lineCap = 'round'

    let frame
    let drawing = false
    let mousePos = { x: 0, y: 0 }
    let lastPos = mousePos
    const isMobile = 'ontouchstart' in window || window.DocumentTouch

    // mouse/touch events
    canvas.addEventListener(isMobile ? 'touchstart' : 'mousedown', (e) => {
      drawing = true
      lastPos = getMousePos(canvas, e)
      mousePos = lastPos
    })

    canvas.addEventListener(isMobile ? 'touchmove' : 'mousemove', (e) => {
      mousePos = getMousePos(canvas, e)
    })

    canvas.addEventListener(isMobile ? 'touchend' : 'mouseup', () => {
      drawing = false
    })

    const getMousePos = (canvasDom, touchOrMouseEvent) => {
      const rect = canvasDom.getBoundingClientRect()
      return {
        x:
          (isMobile
            ? touchOrMouseEvent.touches[0].clientX
            : touchOrMouseEvent.clientX) - rect.left,
        y:
          (isMobile
            ? touchOrMouseEvent.touches[0].clientY
            : touchOrMouseEvent.clientY) - rect.top,
      }
    }

    const renderCanvas = () => {
      if (drawing) {
        ctx.moveTo(lastPos.x, lastPos.y)
        ctx.lineTo(mousePos.x, mousePos.y)
        ctx.stroke()
        lastPos = mousePos
      }
    }

    ;(function loop() {
      frame = requestAnimationFrame(loop)
      renderCanvas()
    })()

    return () => {
      cancelAnimationFrame(frame)
    }
  })

  const toggleNote = () => {
    addNote = !addNote
    note = ''
  }

  const saveSignature = () => {
    inFlight = true
    if (window.navigator.vibrate) {
      navigator.vibrate(120)
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude
        const lng = position.coords.longitude
        const timeStamp = new Date()
        const data = {
          location_id: locationId,
          client_id: clientId,
          assignee: $userStore.name,
          time_stamp: timeStamp,
          coordinates: [lng, lat],
          signature: canvas.toDataURL('image/png', 0.1),
          name,
          note,
        }

        // Submit work log data
        try {
          const response = await request({
            url: `/api/work-logs`,
            method: 'POST',
            body: JSON.stringify({
              ...data,
              tenant: $userStore.tenant_id,
            }),
          })

          // Close Signature Box
          signatureBlockStore.set({ isOpen: false })

          if (response.message) {
            snackBarStore.set({
              message: response.message,
              type: 'warning',
              selfDestruct: true,
            })
          } else {
            // Add newly created work log to the store
            workLogStore.update((state) => [response].concat(state))
            snackBarStore.set({
              message: $t['Log Recorded'],
              type: 'success',
              selfDestruct: true,
            })
          }
        } catch (error) {
          snackBarStore.set({ message: $t.something_wrong, type: 'error' })
        } finally {
          inFlight = false
        }
      },
      (error) => {
        inFlight = false
        snackBarStore.set({ message: $t.location_services, type: 'warning' })
      }
    )
  }
</script>

<section trasition:fade={{ duration: 200 }}>
  <div
    class="form"
    transition:fly|global={{
      duration: 200,
      x: 0,
      y: 20,
      opacity: 0.5,
      easing: quintInOut,
    }}
  >
    <h1>{$t['Sign Below']}</h1>
    <canvas {width} {height} bind:this={canvas} />
    <TextInput
      id="name"
      required="true"
      label={$t.signer_name}
      validityMessage={$t['Please enter a contact name']}
      on:input={(event) => (name = event.target.value)}
      value={name}
    />
    <div class="toggle-note">
      <input
        type="checkbox"
        name="toggle-note"
        id="toggle"
        on:click={toggleNote}
      />
      <label for="toggle">{$t['Add a note']}</label>
    </div>
    {#if addNote}
      <div class="form-control">
        <label for="note">{$t['Notes']}</label>
        <textarea
          name="note"
          id="note"
          cols="10"
          maxlength="140"
          rows="3"
          on:input={(event) => (note = event.target.value)}
        />
        <small>{noteCount} / 140</small>
      </div>
    {/if}
    <div class="button-group">
      <button
        type="button"
        class="cancel"
        on:click={() => signatureBlockStore.set({ isOpen: false })}
        ><Icon name="close" size="x-large" /></button
      >
      {#if name.length > 1}
        <button
          type="button"
          class="check"
          on:click={saveSignature}
          disabled={inFlight}
        >
          {#if inFlight}
            <svg class="spinner" viewBox="0 0 50 50">
              <circle
                class="path"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke-width="5"
              />
            </svg>
          {:else}
            <Icon name="check" size="x-large" />
          {/if}
        </button>
      {/if}
    </div>
  </div>
</section>

<style>
  section {
    position: fixed;
    background-color: var(--bg-overlay);
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100%;
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    flex-direction: column;
  }

  .form {
    background-color: var(--white);
    padding: 4rem 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 95vw;
    min-height: 70vh;
    border-radius: var(--radius);
  }

  h1 {
    text-align: center;
  }

  canvas {
    box-sizing: border-box;
    border: 3px solid var(--grey);
    background-color: var(--white);
    border-radius: var(--radius);
  }

  .button-group {
    display: flex;
    justify-content: space-evenly;
    width: 100%;
  }

  button {
    border-radius: 50%;
    border: 0;
    height: 6rem;
    width: 6rem;
    color: white;
    transition: scale 0.4s;
  }

  button:active {
    scale: 1.6;
  }

  .cancel {
    background-color: var(--red);
  }

  .check {
    background-color: var(--green);
  }

  .spinner {
    animation: rotate 2s linear infinite;
    vertical-align: middle;
    width: 30px;
    height: 30px;
  }

  .path {
    stroke: currentColor;
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes dash {
    0% {
      stroke-dasharray: 1, 150;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -35;
    }
    100% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -124;
    }
  }

  .check:hover,
  .check:active {
    background: #31b51d;
    border-color: #31b51d;
  }

  .check:disabled {
    opacity: 0.8;
  }
</style>
